<template>
  <div>
    <template v-if="!reviewDashboard() && !codingDashboard()">
      <h1 class="page-header">No Access</h1>
      You do not have access to the dashboard. Please contact HIA.
    </template>

    <template v-else>
      <el-breadcrumb separator="/">
        <el-breadcrumb-item>
          <clientNavSelector></clientNavSelector>
        </el-breadcrumb-item>
        <el-breadcrumb-item :to="{ path: `/${currentClientId}/dashboard` }">Coding Support Dashboard</el-breadcrumb-item>
      </el-breadcrumb>
      <div class="top-margin" v-loading="initLoading" element-loading-spinner="nucleus-loader" style="min-height: 200px;">
        <el-date-picker v-model="dateRange" type="daterange" range-separator="To" start-placeholder="Start date" end-placeholder="End date" format="MM/dd/yyyy" value-format="MM-dd-yyyy" style="float: right; z-index: 1000; margin-top: 6px;" @change="handleCriteriaChange"> </el-date-picker>

        <el-tabs id="dashboardTabs" v-model="activeTab">
          <el-tab-pane v-if="codingDashboard()" label="Coding Support" name="coding">
            <CodingDashboard v-loading="loading" element-loading-spinner="nucleus-loader" v-if="!initLoading"></CodingDashboard>
          </el-tab-pane>
          <el-tab-pane v-if="codingDashboard()" lazy label="Coding Schedule" name="review" v-model="activeTab">
            <CodingCalendarSchedule :dateRange="dateRange" :initialProjectId="calendarInitialProjectId"></CodingCalendarSchedule>
          </el-tab-pane>
          <el-tab-pane lazy label="Coding Logs & Reports">
            <el-table class="codingReportsTable" :data="codingReportRows" border style="width: 100%; margin-top: 10px;">
              <el-table-column label="Report Name" width="240">
                <template slot-scope="scope">
                  <span class="codingReportLink" @click="handleReportClick(scope.row)"> <i :class="scope.row.icon" class="codingReportLinkIcon"></i>{{ scope.row.name }} </span>
                </template>
              </el-table-column>
              <el-table-column prop="description" label="Description"></el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </div>

      <el-dialog :title="finalizedDialogTitle" :visible.sync="showFinalizedRecordsModal" width="75%" destroy-on-close>
        <DetailsPopOver v-if="showFinalizedRecordsModal" :isCoding="true" :header="finalizedDialogTitle" :isModal="true" :tableType="'accountNumbers'" :reviewType="'final'" :row="{}"></DetailsPopOver>
      </el-dialog>

      <el-dialog title="Coding Logs" :visible.sync="showCodingLogs" width="50%">
        <CodingLogs :dateRange="dateRange" :isOpen="showCodingLogs"></CodingLogs>
      </el-dialog>
    </template>
  </div>
</template>

<script>
import HiaAuthorization from '@/mixins/hiaAuthorization'
import money from '@/mixins/money'
import { mapState, mapActions, mapMutations } from 'vuex'
import CodingDashboard from '@/views/Dashboards/CodingDashboard'
import CodingCalendarSchedule from '@/views/Dashboards/CodingCalendarSchedule'
import CodingLogs from '@/views/Dashboards/CodingLogs'
import DetailsPopOver from '@/views/Dashboards/DetailsPopOver'
import { format } from 'date-fns'

export default {
  name: 'CodingDashboardLanding',
  mixins: [HiaAuthorization, money],
  data() {
    return {
      loading: false,
      initLoading: false,
      reviewLoading: false,
      dateRange: [],
      activeTab: 'coding',
      showCodingLogs: false,
      showFinalizedRecordsModal: false,
      calendarInitialProjectId: null,
      codingReportRows: [{ action: 'viewCodingLogs', icon: 'mdi mdi-details', name: 'View Coding Logs', description: 'Shows coding activity logs for the selected date range.' }, { action: 'viewCodingLogDetails', icon: 'mdi mdi-file-download-outline tableIcon', name: 'View Coding Log Details', description: 'Provides detailed coding log export data for the selected date range.' }, { action: 'viewCodingSummary', icon: 'mdi mdi-file-document-box-outline', name: 'View Coding Summary', description: 'Generates the coding summary report PDF for the selected date range.' }, { action: 'viewFinal', icon: 'mdi mdi-view-list', name: 'View Finalized Records', description: 'Shows finalized account records for the selected date range.' }]
    }
  },
  components: {
    CodingDashboard,
    CodingCalendarSchedule,
    CodingLogs,
    DetailsPopOver
  },
  methods: {
    ...mapActions('reports/', ['GENERATE_REPORT']),
    ...mapActions('dashboard/', ['GET_DASHBOARD', 'GET_CODINGSUMMARY_PDF_URL']),
    ...mapMutations('dashboard/', ['SET_CODING_DATE_RANGE']),
    async initialize() {
      this.applyRouteQuery()
      let now = new Date()
      this.dateRange[0] = format(new Date(now.getFullYear(), now.getMonth(), 1), 'MM-dd-yyyy')
      this.dateRange[1] = format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'MM-dd-yyyy')
      this.SET_CODING_DATE_RANGE(this.dateRange)
      let payload = {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1]
      }
      this.loading = true
      this.initLoading = true

      await this.GET_DASHBOARD(payload)
      this.loading = false
      this.initLoading = false
    },
    async handleCriteriaChange() {
      this.SET_CODING_DATE_RANGE(this.dateRange)

      let payload = {
        currentClient: this.currentClient,
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        currentlyPending: this.currentlyPending
      }
      this.loading = true
      await this.GET_DASHBOARD(payload)
      this.loading = false
    },
    applyRouteQuery() {
      const query = (this.$route && this.$route.query) || {}
      const hasQueryParams = Object.keys(query).length > 0
      const activeTab = query.activeTab != null ? String(query.activeTab).toLowerCase() : ''
      const initialProjectId = query.initialProjectId != null && query.initialProjectId !== '' ? Number(query.initialProjectId) : null

      if (activeTab === 'codingschedule' && this.codingDashboard()) {
        this.activeTab = 'review'
      }

      this.calendarInitialProjectId = Number.isFinite(initialProjectId) ? initialProjectId : null

      // Clear processed query params from URL after handling deep-link state.
      if (hasQueryParams) {
        const cleanUrl = `${window.location.origin}${this.$route.path}`
        window.history.replaceState({}, '', cleanUrl)
      }
    },
    viewCodingSummaryReport() {
      let payload = {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1]
      }

      const loading = this.$loading({
        lock: true,
        text: 'Getting ready...',
        spinner: 'nucleus-loader',
        background: 'rgba(255, 255, 255, 0.7)'
      })

      this.GET_CODINGSUMMARY_PDF_URL(payload).then(response => {
        loading.close()
        window.open(`/static/pdfjs/web/viewer.html?file=${response}`, '_blank')
      })
    },
    handleReportClick(row) {
      if (!row || !row.action) return
      switch (row.action) {
        case 'viewCodingSummary':
          this.viewCodingSummaryReport()
          break
        case 'viewFinal':
          this.showFinalizedRecordsModal = true
          break
        case 'viewCodingLogs':
          this.showCodingLogs = true
          break
        case 'viewCodingLogDetails':
          this.viewCodingLogDetailsReport()
          break
      }
    },
    viewCodingLogDetailsReport() {
      let payload = {
        startDate: this.dateRange[0],
        endDate: this.dateRange[1],
        body: {
          id: 8,
          reportName: 'Coding Log Details',
          reportPath: 'Client_CodingDetails',
          reportDescription: 'Provides Hours Worked, and a listing of all accounts coded/finalized for each day in the date range.',
          clientNumberParameterName: 'ClientNumber',
          reportType: 1
        }
      }

      const loading = this.$loading({
        lock: true,
        text: 'Getting ready...',
        spinner: 'nucleus-loader',
        background: 'rgba(255, 255, 255, 0.7)'
      })

      this.GENERATE_REPORT(payload).then(
        response => {
          loading.close()
          let fileName = this.getFileNameFromResponse(response)
          this.generateDownloadLink(response.data, fileName)
        },
        error => {
          this.$message.error('Error generating report')
          console.log(error)
        }
      )
    },
    generateDownloadLink(file, fileName) {
      let blob = new Blob([file], { type: 'application/octet-stream' })
      const link = document.createElement('a')
      link.setAttribute('style', 'display: hidden;')
      link.id = 'hiddenLink'
      link.href = window.URL.createObjectURL(blob)
      link.download = fileName
      document.getElementsByTagName('body')[0].appendChild(link)
      document.getElementById('hiddenLink').click()
      const timerId = setInterval(() => {
        document.getElementById('hiddenLink').remove()
        clearInterval(timerId)
      }, 500)
    },
    getFileNameFromResponse(response) {
      var contdisp = response.headers['content-disposition']
      var parts = contdisp.split('; ')
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].indexOf('filename=') > -1) {
          let filename = parts[1].replace('filename=', '')
          filename = filename.replace(/"/g, '')
          return filename
        }
      }
    }
  },
  created: function() {
    this.initialize()
  },
  watch: {
    '$route.query': {
      handler() {
        this.applyRouteQuery()
      },
      deep: true
    }
  },
  computed: {
    ...mapState('user/', ['currentClient', 'currentClientId']),
    ...mapState('dashboard/', ['dashboardData', 'reviewDashboardData']),
    finalizedDialogTitle() {
      return `Finalized Accounts from ${this.dateRange[0]} to ${this.dateRange[1]}`
    }
  }
}
</script>

<style scoped>
.dataTables .caret-wrapper {
  display: none !important;
}

.dataTables thead th .cell:hover {
  color: #005695;
  text-decoration: underline;
}

.dataTables .el-tooltip {
  width: 90% !important;
}

>>> .el-table__expanded-cell {
  padding-left: 1% !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
  padding-right: 0px !important;
}

>>> #queryDrillDownTable thead {
  display: none;
}

>>> #pendingDrillDownTable thead {
  display: none;
}

.dataTables >>> .el-table__body,
>>> .el-table__header {
  width: 100% !important;
  table-layout: inherit !important;
}

>>> .parentLargeCell {
  width: 16%;
}

>>> .childLargeCell {
  width: 27%;
}

>>> .smallCell {
  width: 12%;
}

>>> .parentLargeCell .cell,
>>> .childLargeCell .cell,
>>> .smallCell .cell {
  max-width: 100% !important;
}

#codingLargeStat2 >>> .el-table__expand-icon {
  float: left;
}

>>> .dottedUnderline {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
}

.coding {
  /* background: rgba(255, 196, 12, 1) !important; */
}

.review {
  /* background: rgba(236, 0, 140, 1) !important; */
}

.profee {
  /* background: rgba(109, 207, 246, 1) !important; */
}

.learn {
  /* background: rgba(136, 185, 91, 1) !important; */
}

>>> .smallCardPadding .el-card__body {
  padding: 5px !important;
}

>>> .dataTitle {
  display: block;
  font-size: 16px;
  font-weight: 400;
  text-align: left;
  color: #606266;
}

>>> .dataText {
  display: block;
  font-size: 30px;
  color: #606266;
  text-align: left !important;
  font-weight: 400;
  padding: 5px 0px 5px 0px;
}

>>> .dataFooter {
  display: block;
  font-size: 12px;
  color: #606266;
  text-align: left !important;
  font-weight: 400;
}

>>> .dataFooter a {
  /* color: #606266;
      font-size: 14px; */
}

>>> .dataCard {
  /* height: 65px; */
  width: 100%;
  height: 100%;
}

>>> .dataCard .el-card__header {
  /* padding: 8px 10px;
      font-size: 18px;
      font-weight: 400; */
}

>>> .dataCard .el-table {
  overflow: auto;
}

>>> .dataCard .el-card__body {
  padding: 5px;
}

>>> .dataCard .el-table .cell {
  padding-left: 0px;
  padding-right: 0px;
}

>>> .codingCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  background: rgba(255, 196, 12, 0.8);
}

>>> .reviewCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  /* background: rgba(236, 0, 140, .8); */
  background: rgba(19, 86, 149, 0.8);
}

>>> .reportCard .el-card__header {
  padding: 8px 10px;
  font-size: 18px;
  font-weight: 400;
  color: #ffffff;
  background: rgba(0, 86, 149, 0.8);
}

>>> .el-table .cell {
  word-break: normal;
}

#clientTitle {
  text-align: left;
  font-size: 20px;
}

#dashboardTabs {
  margin: 0px 0px 0px 0px;
}

#dashboardTabs >>> .el-tabs__nav-wrap::after {
  background-color: transparent;
}

#dashboardTabs >>> .el-tabs__content {
  margin-top: 20px;
  overflow: inherit;
}

#codingLargeStat1 >>> .el-table__body-wrapper {
  overflow-x: inherit;
}

#codingLargeStat1 >>> .avatar {
  display: block !important;
}

.codingReportsTable >>> .cell {
  padding-top: 10px;
  padding-bottom: 10px;
  font-size: 14px;
}

.codingReportLink {
  color: #005695;
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
}

.codingReportLinkIcon {
  margin-right: 6px;
}
</style>
